/**
 * Project Templates
 * 
 * Multi-framework templates for AppBuilder projects.
 * Supports React SPAs, Next.js projects, and static sites.
 */

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  category: 'react-spa' | 'nextjs' | 'static';
  icon: string;
  projectKind: 'react-spa' | 'nextjs' | 'static';
  capabilities: string[];
  files: Record<string, string>;
  dependencies?: Record<string, string>;
}

export const PROJECT_TEMPLATES: Record<string, ProjectTemplate> = {
  'react-spa': {
    id: 'react-spa',
    name: 'React App',
    description: 'Single-page React application with TypeScript',
    category: 'react-spa',
    icon: 'Code',
    projectKind: 'react-spa',
    capabilities: ['river-publish', 'river-subdomain'],
    files: {
      'src/App.tsx': getReactAppTemplate(),
      'src/App.css': getReactCSSTemplate(),
    },
    dependencies: {
      'react': '^18.2.0',
      'react-dom': '^18.2.0',
    }
  },
  
  'nextjs-shop': {
    id: 'nextjs-shop',
    name: 'Next.js Shop',
    description: 'E-commerce site with Stripe checkout and River products',
    category: 'nextjs',
    icon: 'ShoppingCart',
    projectKind: 'nextjs',
    capabilities: ['vercel-deploy', 'stripe-checkout', 'river-products'],
    files: {
      'app/layout.tsx': getNextLayoutTemplate(),
      'app/page.tsx': getNextHomeTemplate(),
      'app/globals.css': getGlobalsCSSTemplate(),
      'app/shop/page.tsx': getNextShopListingTemplate(),
      'app/shop/[id]/page.tsx': getNextProductDetailTemplate(),
      'app/cart/page.tsx': getNextCartTemplate(),
      'app/api/checkout/route.ts': getNextCheckoutApiTemplate(),
      'components/ProductCard.tsx': getProductCardTemplate(),
      'components/Navbar.tsx': getNavbarTemplate(),
      'lib/stripe.ts': getStripeClientTemplate(),
      'lib/products.ts': getProductsLibTemplate(),
      'next.config.js': getNextConfigTemplate(),
      'package.json': getNextPackageJsonTemplate('shop'),
      'tsconfig.json': getNextTsConfigTemplate(),
      'tailwind.config.js': getTailwindConfigTemplate(),
      'postcss.config.js': getPostcssConfigTemplate(),
      '.env.local.example': getEnvExampleTemplate(),
    },
    dependencies: {
      'react': '^18.2.0',
      'react-dom': '^18.2.0',
      'next': '^14.1.0',
      'stripe': '^14.14.0',
      '@stripe/stripe-js': '^2.4.0',
    }
  },
  
  'nextjs-shop-simple': {
    id: 'nextjs-shop-simple',
    name: 'Next.js Shop (Simple)',
    description: 'Self-contained e-commerce shop — products hardcoded, no River API at runtime',
    category: 'nextjs',
    icon: 'ShoppingCart',
    projectKind: 'nextjs',
    capabilities: ['vercel-deploy', 'stripe-checkout'],
    files: {
      'app/layout.tsx': getNextLayoutTemplate(),
      'app/page.tsx': getNextShopSimpleHomeTemplate(),
      'app/globals.css': getGlobalsCSSTemplate(),
      'app/shop/page.tsx': getNextShopSimpleListingTemplate(),
      'app/cart/page.tsx': getNextCartTemplate(),
      'app/api/checkout/route.ts': getNextCheckoutApiTemplate(),
      'components/Navbar.tsx': getNavbarTemplate(),
      'lib/products.ts': getProductsSimpleLibTemplate(),
      'next.config.js': getNextConfigTemplate(),
      'package.json': getNextPackageJsonTemplate('shop'),
      'tsconfig.json': getNextTsConfigTemplate(),
      'tailwind.config.js': getTailwindConfigTemplate(),
      'postcss.config.js': getPostcssConfigTemplate(),
      '.env.local.example': getEnvSimpleExampleTemplate(),
    },
    dependencies: {
      'react': '^18.2.0',
      'react-dom': '^18.2.0',
      'next': '^14.1.0',
      'stripe': '^14.14.0',
      '@stripe/stripe-js': '^2.4.0',
    }
  },
  
  'nextjs-blog': {
    id: 'nextjs-blog',
    name: 'Next.js Blog',
    description: 'Modern blog with MDX support and SEO optimization',
    category: 'nextjs',
    icon: 'Article',
    projectKind: 'nextjs',
    capabilities: ['vercel-deploy', 'static-export', 'mdx-support'],
    files: {
      'app/layout.tsx': getNextLayoutTemplate(),
      'app/page.tsx': getNextBlogHomeTemplate(),
      'app/globals.css': getGlobalsCSSTemplate(),
      'app/blog/[slug]/page.tsx': getNextBlogPostTemplate(),
      'components/PostCard.tsx': getPostCardTemplate(),
      'lib/posts.ts': getPostsLibTemplate(),
      'content/posts/welcome.mdx': getFirstPostTemplate(),
      'next.config.js': getNextConfigTemplate(),
      'package.json': getNextPackageJsonTemplate('blog'),
      'tsconfig.json': getNextTsConfigTemplate(),
      'tailwind.config.js': getTailwindConfigTemplate(),
      'postcss.config.js': getPostcssConfigTemplate(),
    },
    dependencies: {
      'react': '^18.2.0',
      'react-dom': '^18.2.0',
      'next': '^14.1.0',
      '@next/mdx': '^14.1.0',
      'gray-matter': '^4.0.3',
    }
  },
  
  'nextjs-portfolio': {
    id: 'nextjs-portfolio',
    name: 'Next.js Portfolio',
    description: 'Personal portfolio with project showcase',
    category: 'nextjs',
    icon: 'User',
    projectKind: 'nextjs',
    capabilities: ['vercel-deploy', 'static-export'],
    files: {
      'app/layout.tsx': getNextLayoutTemplate(),
      'app/page.tsx': getNextPortfolioHomeTemplate(),
      'app/globals.css': getGlobalsCSSTemplate(),
      'app/projects/page.tsx': getNextProjectsTemplate(),
      'app/about/page.tsx': getNextAboutTemplate(),
      'components/ProjectCard.tsx': getProjectCardTemplate(),
      'components/Hero.tsx': getHeroTemplate(),
      'next.config.js': getNextConfigTemplate(),
      'package.json': getNextPackageJsonTemplate('portfolio'),
      'tsconfig.json': getNextTsConfigTemplate(),
      'tailwind.config.js': getTailwindConfigTemplate(),
      'postcss.config.js': getPostcssConfigTemplate(),
    },
    dependencies: {
      'react': '^18.2.0',
      'react-dom': '^18.2.0',
      'next': '^14.1.0',
    }
  },
};

function getNextLayoutTemplate(): string {
  return `import type { Metadata } from 'next'
import './globals.css'

export const metadata: Metadata = {
  title: 'My Next.js App',
  description: 'Built with River & Next.js',
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <html lang="en">
      <body>{children}</body>
    </html>
  )
}`;
}

function getGlobalsCSSTemplate(): string {
  return `@tailwind base;
@tailwind components;
@tailwind utilities;

body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}`;
}

function getNextHomeTemplate(): string {
  return `export default function Home() {
  return (
    <main className="min-h-screen p-24">
      <h1 className="text-4xl font-bold mb-4">
        Welcome to Your Next.js App
      </h1>
      <p className="text-lg text-gray-600">
        Built with River & deployed to Vercel
      </p>
    </main>
  )
}`;
}

function getNextShopListingTemplate(): string {
  return `'use client'

import { useEffect, useState } from 'react'
import ProductCard from '@/components/ProductCard'

interface Product {
  _id: string
  name: string
  skeleton: {
    price: number
    description: string
    imageUrl: string
    stripePriceId: string
    inStock: boolean
  }
}

export default function ShopPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_RIVER_API_URL || 'https://api.rivereditor.com'
    const websiteId = process.env.NEXT_PUBLIC_WEBSITE_ID
    const orgId = process.env.NEXT_PUBLIC_ORGANIZATION_ID
    fetch(\`\${apiUrl}/api/public/entities/query\`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityType: 'Product',
        ...(websiteId ? { websiteId } : orgId ? { organizationId: orgId } : {}),
        limit: 100
      })
    })
    .then(res => res.json())
    .then(data => {
      setProducts(Array.isArray(data) ? data : [])
      setLoading(false)
    })
    .catch(err => {
      console.error('Failed to fetch products:', err)
      setLoading(false)
    })
  }, [])

  if (loading) {
    return (
      <main className="container mx-auto px-4 py-8">
        <div className="text-xl">Loading products...</div>
      </main>
    )
  }
  
  return (
    <main className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Shop All Products</h1>
      {products.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No products available yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      )}
    </main>
  )
}`;
}

function getNextProductDetailTemplate(): string {
  return `'use client'

import { useEffect, useState } from 'react'
import { notFound } from 'next/navigation'

interface Product {
  _id: string
  name: string
  skeleton: {
    price: number
    description: string
    imageUrl: string
    stripePriceId: string
    inStock: boolean
  }
}

interface Props {
  params: { id: string }
}

export default function ProductPage({ params }: Props) {
  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  
  useEffect(() => {
    const apiUrl = process.env.NEXT_PUBLIC_RIVER_API_URL || 'https://api.rivereditor.com'
    fetch(\`\${apiUrl}/api/public/entities/\${params.id}\`)
      .then(res => res.json())
      .then(data => {
        setProduct(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Failed to fetch product:', err)
        setLoading(false)
      })
  }, [params.id])
  
  if (loading) {
    return (
      <main className="container mx-auto px-4 py-8">
        <div className="text-xl">Loading...</div>
      </main>
    )
  }
  
  if (!product) {
    return (
      <main className="container mx-auto px-4 py-8">
        <div className="text-xl">Product not found</div>
      </main>
    )
  }
  
  return (
    <main className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <img 
            src={product.skeleton.imageUrl} 
            alt={product.name}
            className="w-full rounded-lg"
          />
        </div>
        <div>
          <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
          <p className="text-2xl font-bold mb-4">
            \${(product.skeleton.price / 100).toFixed(2)}
          </p>
          <p className="text-gray-600 mb-6">{product.skeleton.description}</p>
          <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition">
            Add to Cart
          </button>
        </div>
      </div>
    </main>
  )
}`;
}

function getNextCartTemplate(): string {
  return `'use client'

import { useState } from 'react'

export default function CartPage() {
  const [loading, setLoading] = useState(false)
  
  const handleCheckout = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
          ]
        })
      })
      
      const { url } = await response.json()
      window.location.href = url
    } catch (error) {
      console.error('Checkout failed:', error)
      setLoading(false)
    }
  }
  
  return (
    <main className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Shopping Cart</h1>
      <button
        onClick={handleCheckout}
        disabled={loading}
        className="bg-blue-600 text-white px-6 py-3 rounded-lg"
      >
        {loading ? 'Processing...' : 'Checkout'}
      </button>
    </main>
  )
}`;
}

function getNextCheckoutApiTemplate(): string {
  return `import { NextRequest, NextResponse } from 'next/server'
import Stripe from 'stripe'

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
  apiVersion: '2023-10-16',
})

export async function POST(req: NextRequest) {
  try {
    const { items } = await req.json()
    
    const session = await stripe.checkout.sessions.create({
      line_items: items.map((item: any) => ({
        price: item.stripePriceId,
        quantity: item.quantity,
      })),
      mode: 'payment',
      success_url: \`\${req.nextUrl.origin}/success\`,
      cancel_url: \`\${req.nextUrl.origin}/cart\`,
    })
    
    return NextResponse.json({ url: session.url })
  } catch (error) {
    console.error('Checkout error:', error)
    return NextResponse.json(
      { error: 'Checkout failed' },
      { status: 500 }
    )
  }
}`;
}

function getProductsLibTemplate(): string {
  return `interface Product {
  _id: string
  name: string
  skeleton: {
    price: number
    description: string
    imageUrl: string
    stripePriceId: string
    inStock: boolean
  }
}

const API_URL = process.env.NEXT_PUBLIC_RIVER_API_URL || 'https://api.rivereditor.com'
const WEBSITE_ID = process.env.NEXT_PUBLIC_WEBSITE_ID
const ORG_ID = process.env.NEXT_PUBLIC_ORGANIZATION_ID

export async function getProducts(): Promise<Product[]> {
  const response = await fetch(
    \`\${API_URL}/api/public/entities/query\`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entityType: 'Product',
        ...(WEBSITE_ID ? { websiteId: WEBSITE_ID } : ORG_ID ? { organizationId: ORG_ID } : {}),
        limit: 100,
      }),
      next: { revalidate: 60 }
    }
  )
  
  if (!response.ok) {
    throw new Error(\`Failed to fetch products: \${response.status}\`)
  }
  
  const data = await response.json()
  return Array.isArray(data) ? data : []
}

export async function getProduct(id: string): Promise<Product | null> {
  const response = await fetch(\`\${API_URL}/api/public/entities/\${id}\`)
  
  if (!response.ok) {
    return null
  }
  
  return response.json()
}`;
}

function getStripeClientTemplate(): string {
  return `import { loadStripe } from '@stripe/stripe-js'

export const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!
)`;
}

function getProductCardTemplate(): string {
  return `import Link from 'next/link'

interface Product {
  _id: string
  name: string
  skeleton: {
    price: number
    imageUrl: string
    description: string
  }
}

export default function ProductCard({ product }: { product: Product }) {
  return (
    <Link href={\`/shop/\${product._id}\`}>
      <div className="border rounded-lg p-4 hover:shadow-lg transition">
        <img
          src={product.skeleton.imageUrl}
          alt={product.name}
          className="w-full h-48 object-cover rounded-lg mb-4"
        />
        <h3 className="font-bold text-lg mb-2">{product.name}</h3>
        <p className="text-gray-600 text-sm mb-2">
          {product.skeleton.description.slice(0, 100)}...
        </p>
        <p className="font-bold text-lg">
          \${(product.skeleton.price / 100).toFixed(2)}
        </p>
      </div>
    </Link>
  )
}`;
}

function getNavbarTemplate(): string {
  return `import Link from 'next/link'

export default function Navbar() {
  return (
    <nav className="border-b">
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <Link href="/" className="text-xl font-bold">
          My Shop
        </Link>
        <div className="flex gap-6">
          <Link href="/shop">Shop</Link>
          <Link href="/cart">Cart</Link>
        </div>
      </div>
    </nav>
  )
}`;
}

function getNextConfigTemplate(): string {
  return `/** @type {import('next').NextConfig} */
const nextConfig = {
  images: {
    domains: ['images.unsplash.com', 'via.placeholder.com'],
  },
}

module.exports = nextConfig`;
}

function getNextPackageJsonTemplate(type: 'shop' | 'blog' | 'portfolio'): string {
  const baseDeps = {
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "next": "^14.1.0"
  };
  
  const shopDeps = {
    "stripe": "^14.14.0",
    "@stripe/stripe-js": "^2.4.0"
  };
  
  const blogDeps = {
    "@next/mdx": "^14.1.0",
    "gray-matter": "^4.0.3"
  };
  
  const deps = type === 'shop' ? { ...baseDeps, ...shopDeps } :
               type === 'blog' ? { ...baseDeps, ...blogDeps } :
               baseDeps;
  
  return JSON.stringify({
    "name": `nextjs-${type}`,
    "version": "1.0.0",
    "private": true,
    "scripts": {
      "dev": "next dev",
      "build": "next build",
      "start": "next start",
      "lint": "next lint"
    },
    "dependencies": deps,
    "devDependencies": {
      "@types/node": "^20.11.0",
      "@types/react": "^18.2.0",
      "@types/react-dom": "^18.2.0",
      "typescript": "^5.3.0",
      "tailwindcss": "^3.4.0",
      "postcss": "^8.4.0",
      "autoprefixer": "^10.4.0"
    }
  }, null, 2);
}

function getVercelConfigTemplate(): string {
  return JSON.stringify({
    "buildCommand": "npm run build",
    "outputDirectory": ".next",
    "installCommand": "npm install",
    "framework": "nextjs"
  }, null, 2);
}

function getNextTsConfigTemplate(): string {
  return JSON.stringify({
    "compilerOptions": {
      "target": "ES2017",
      "lib": ["dom", "dom.iterable", "esnext"],
      "allowJs": true,
      "skipLibCheck": true,
      "strict": true,
      "forceConsistentCasingInFileNames": true,
      "noEmit": true,
      "esModuleInterop": true,
      "module": "esnext",
      "moduleResolution": "bundler",
      "resolveJsonModule": true,
      "isolatedModules": true,
      "jsx": "preserve",
      "incremental": true,
      "plugins": [{ "name": "next" }],
      "paths": {
        "@/*": ["./*"]
      }
    },
    "include": ["next-env.d.ts", "**/*.ts", "**/*.tsx", ".next/types/**/*.ts"],
    "exclude": ["node_modules"]
  }, null, 2);
}

function getEnvExampleTemplate(): string {
  return `# River API Configuration
NEXT_PUBLIC_RIVER_API_URL=https://api.rivereditor.com
NEXT_PUBLIC_ORGANIZATION_ID=your_org_id

# Stripe Configuration
NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=pk_test_...
STRIPE_SECRET_KEY=sk_test_...`;
}

function getNextBlogHomeTemplate(): string {
  return `import Link from 'next/link'
import { getPosts } from '@/lib/posts'
import PostCard from '@/components/PostCard'

export default async function BlogHome() {
  const posts = await getPosts()
  
  return (
    <main className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8">Blog</h1>
      <div className="space-y-6">
        {posts.map((post) => (
          <PostCard key={post.slug} post={post} />
        ))}
      </div>
    </main>
  )
}`;
}

function getNextBlogPostTemplate(): string {
  return `import { getPost, getAllPostSlugs } from '@/lib/posts'
import { notFound } from 'next/navigation'

export async function generateStaticParams() {
  const slugs = await getAllPostSlugs()
  return slugs.map((slug) => ({ slug }))
}

export default async function BlogPost({ params }: { params: { slug: string } }) {
  const post = await getPost(params.slug)
  
  if (!post) {
    notFound()
  }
  
  return (
    <article className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
      <time className="text-gray-500 mb-8 block">{post.date}</time>
      <div className="prose lg:prose-xl">
        {post.content}
      </div>
    </article>
  )
}`;
}

function getPostsLibTemplate(): string {
  return `import fs from 'fs'
import path from 'path'
import matter from 'gray-matter'

const postsDirectory = path.join(process.cwd(), 'content/posts')

export async function getPosts() {
  const fileNames = fs.readdirSync(postsDirectory)
  const posts = fileNames.map((fileName) => {
    const slug = fileName.replace(/\\.mdx$/, '')
    const fullPath = path.join(postsDirectory, fileName)
    const fileContents = fs.readFileSync(fullPath, 'utf8')
    const { data } = matter(fileContents)
    
    return {
      slug,
      title: data.title,
      date: data.date,
      excerpt: data.excerpt,
    }
  })
  
  return posts.sort((a, b) => (a.date < b.date ? 1 : -1))
}

export async function getPost(slug: string) {
  const fullPath = path.join(postsDirectory, \`\${slug}.mdx\`)
  
  if (!fs.existsSync(fullPath)) {
    return null
  }
  
  const fileContents = fs.readFileSync(fullPath, 'utf8')
  const { data, content } = matter(fileContents)
  
  return {
    slug,
    title: data.title,
    date: data.date,
    content,
  }
}

export async function getAllPostSlugs() {
  const fileNames = fs.readdirSync(postsDirectory)
  return fileNames.map((fileName) => fileName.replace(/\\.mdx$/, ''))
}`;
}

function getFirstPostTemplate(): string {
  return `---
title: "Welcome to My Blog"
date: "2026-02-20"
excerpt: "This is my first blog post built with Next.js and River."
---

# Welcome to My Blog

This is my first blog post built with Next.js and River.

## Getting Started

You can edit this post by modifying the content files or asking the AI to create new posts for you.

## Features

- Fast and SEO-friendly
- Deployed on Vercel
- Easy to customize
`;
}

function getNextPortfolioHomeTemplate(): string {
  return `import Hero from '@/components/Hero'
import Link from 'next/link'

export default function Home() {
  return (
    <main>
      <Hero />
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold mb-8">Featured Projects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Link href="/projects" className="text-blue-600">
            View all projects →
          </Link>
        </div>
      </section>
    </main>
  )
}`;
}

function getNextProjectsTemplate(): string {
  return `export default function ProjectsPage() {
  const projects = [
    { id: 1, title: 'Project 1', description: 'Description here' },
    { id: 2, title: 'Project 2', description: 'Description here' },
  ]
  
  return (
    <main className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-8">My Projects</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {projects.map((project) => (
          <div key={project.id} className="border rounded-lg p-6">
            <h3 className="text-xl font-bold mb-2">{project.title}</h3>
            <p className="text-gray-600">{project.description}</p>
          </div>
        ))}
      </div>
    </main>
  )
}`;
}

function getNextAboutTemplate(): string {
  return `export default function AboutPage() {
  return (
    <main className="container mx-auto px-4 py-8 max-w-3xl">
      <h1 className="text-4xl font-bold mb-8">About Me</h1>
      <div className="prose lg:prose-xl">
        <p>
          Hi, I'm a developer who loves building things with React and Next.js.
        </p>
        <p>
          This portfolio is built with River and deployed on Vercel.
        </p>
      </div>
    </main>
  )
}`;
}

function getProjectCardTemplate(): string {
  return `interface Project {
  id: number
  title: string
  description: string
  imageUrl?: string
  tags?: string[]
}

export default function ProjectCard({ project }: { project: Project }) {
  return (
    <div className="border rounded-lg overflow-hidden hover:shadow-lg transition">
      {project.imageUrl && (
        <img src={project.imageUrl} alt={project.title} className="w-full h-48 object-cover" />
      )}
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2">{project.title}</h3>
        <p className="text-gray-600 mb-4">{project.description}</p>
        {project.tags && (
          <div className="flex gap-2 flex-wrap">
            {project.tags.map((tag) => (
              <span key={tag} className="bg-gray-100 px-2 py-1 rounded text-sm">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}`;
}

function getHeroTemplate(): string {
  return `export default function Hero() {
  return (
    <section className="bg-gradient-to-r from-blue-500 to-purple-600 text-white py-24">
      <div className="container mx-auto px-4 text-center">
        <h1 className="text-5xl font-bold mb-4">
          Hi, I'm [Your Name]
        </h1>
        <p className="text-xl mb-8">
          I build modern web applications with React & Next.js
        </p>
        <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold">
          View My Work
        </button>
      </div>
    </section>
  )
}`;
}

function getPostCardTemplate(): string {
  return `import Link from 'next/link'

interface Post {
  slug: string
  title: string
  excerpt: string
  date: string
}

export default function PostCard({ post }: { post: Post }) {
  return (
    <article className="border rounded-lg p-6 hover:shadow-lg transition">
      <Link href={\`/blog/\${post.slug}\`}>
        <h2 className="text-2xl font-bold mb-2">{post.title}</h2>
        <p className="text-gray-600 mb-4">{post.excerpt}</p>
        <time className="text-sm text-gray-500">{post.date}</time>
      </Link>
    </article>
  )
}`;
}

function getReactAppTemplate(): string {
  return `import React from 'react';
import './App.css';

interface AppProps {
  windowId: string;
  spaceId: string;
}

export const App: React.FC<AppProps> = ({ windowId, spaceId }) => {
  return (
    <div className="app-shell">
      <div className="app-shell__card">
        <div className="app-shell__icon">✦</div>
        <h1 className="app-shell__title">Your app will appear here</h1>
        <p className="app-shell__subtitle">
          Describe what you want to build and the AI will generate it for you.
        </p>
      </div>
    </div>
  );
};

export default App;`;
}

function getReactCSSTemplate(): string {
  return `* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: #f9fafb;
}

.app-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
}

.app-shell__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  max-width: 360px;
}

.app-shell__icon {
  font-size: 32px;
  color: #6366f1;
  line-height: 1;
  margin-bottom: 4px;
}

.app-shell__title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.01em;
}

.app-shell__subtitle {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
}`;
}

function getTailwindConfigTemplate(): string {
  return `/** @type {import('tailwindcss').Config} */
module.exports = {
  content: [
    './app/**/*.{js,ts,jsx,tsx,mdx}',
    './components/**/*.{js,ts,jsx,tsx,mdx}',
  ],
  theme: {
    extend: {},
  },
  plugins: [],
}`;
}

function getPostcssConfigTemplate(): string {
  return `module.exports = {
  plugins: {
    tailwindcss: {},
    autoprefixer: {},
  },
}`;
}

// ─────────────────────────────────────────────────────────────
// nextjs-shop-simple starters
// The AI replaces these with real data after use_template fires.
// ─────────────────────────────────────────────────────────────

function getNextShopSimpleHomeTemplate(): string {
  return `import { PRODUCTS, formatPrice } from '@/lib/products'
import Link from 'next/link'

export default function Home() {
  const featured = PRODUCTS.slice(0, 3)

  return (
    <main className="min-h-screen">
      <section className="py-24 px-6 text-center bg-gray-50">
        <h1 className="text-5xl font-bold mb-4">Your Shop</h1>
        <p className="text-xl text-gray-500 mb-8">Quality products, fast shipping</p>
        <Link href="/shop"
          className="inline-block px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors">
          Shop Now
        </Link>
      </section>

      <section className="py-20 px-6 max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold mb-10">Featured Products</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {featured.map(product => (
            <Link key={product.id} href={'/product/' + product.id}
              className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
              <div className="aspect-square bg-gray-50 overflow-hidden">
                <img src={product.imageUrl} alt={product.name}
                  className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-4">
                <h3 className="font-medium">{product.name}</h3>
                <p className="text-gray-500 text-sm mt-1">{formatPrice(product.price, product.currency)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  )
}`;
}

function getNextShopSimpleListingTemplate(): string {
  return `import { PRODUCTS, formatPrice } from '@/lib/products'
import Link from 'next/link'

export default function ShopPage() {
  return (
    <main className="max-w-5xl mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold mb-10">All Products</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PRODUCTS.map(product => (
          <Link key={product.id} href={'/product/' + product.id}
            className="border border-gray-200 rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
            <div className="aspect-square bg-gray-50">
              <img src={product.imageUrl} alt={product.name}
                className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-4">
              <h3 className="font-medium">{product.name}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description}</p>
              <p className="font-semibold mt-2">{formatPrice(product.price, product.currency)}</p>
            </div>
          </Link>
        ))}
      </div>
    </main>
  )
}`;
}

function getProductsSimpleLibTemplate(): string {
  return `// Products are hardcoded here — no API calls needed at runtime.
// The AI will overwrite this with real CJDropshipping data + real Stripe price IDs
// during the setup phase using use_template({ templateId: "nextjs-shop-simple" }).

export interface Product {
  id: string
  name: string
  price: number          // retail price in cents
  costPrice: number      // wholesale price in cents (for tracking)
  currency: string
  description: string
  imageUrl: string       // real image URL from supplier
  inStock: boolean
  category: string
  stripePriceId: string  // real Stripe price_xxx ID
  cjPid: string          // CJDropshipping product pid (for fulfillment)
  shippingTime?: string
}

// Replace this array with real products from CJDropshipping
export const PRODUCTS: Product[] = [
  {
    id: 'product-001',
    name: 'Sample Product',
    price: 2999,
    costPrice: 1800,
    currency: 'usd',
    description: 'A sample product — replace with real data',
    imageUrl: 'https://via.placeholder.com/400x400',
    inStock: true,
    category: 'General',
    stripePriceId: 'price_REPLACE_ME',
    cjPid: 'REPLACE_ME',
    shippingTime: '7-14 days',
  },
]

export function getProducts(): Product[] {
  return PRODUCTS.filter(p => p.inStock)
}

export function getProduct(id: string): Product | undefined {
  return PRODUCTS.find(p => p.id === id)
}

export function formatPrice(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}`;
}

function getEnvSimpleExampleTemplate(): string {
  return `# Stripe Configuration (required)
STRIPE_SECRET_KEY=sk_test_...
STRIPE_WEBHOOK_SECRET=whsec_...
NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=pk_test_...
NEXT_PUBLIC_APP_URL=https://your-shop.vercel.app

# CJDropshipping — optional, enables automatic order fulfillment
# If not set, orders are logged but not automatically submitted to CJ
CJ_EMAIL=your@email.com
CJ_PASSWORD=your_cj_password

# No NEXT_PUBLIC_RIVER_API_URL needed — this shop has no River dependency at runtime`;
}
